/* src/constants/bdcStatusMeta.ts
 *
 * Front-end reference table for Booking.com property statuses.
 * “canOpen”  – enable “Open property” menu item
 * “canClose” – enable “Close property” menu item
 */

export interface BdcStatusMeta {
  code: string // exact status string returned in Property-Details.status
  canOpen: boolean
  canClose: boolean
  canEdit: boolean
  readonlyFields?: string[]
}

const readonlyFields = [
  'currency_code',
  'primary_language',
  'physical_address.address_line',
  'physical_address.city_name',
  'physical_address.country_code',
  'physical_address.postal_code',
]

export const bdcStatusMeta: BdcStatusMeta[] = [
  /* ───────── BOOKABLE / BUILD STAGES ───────── */
  { code: 'Open / bookable', canOpen: false, canClose: true, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'XML: Being built', canOpen: true, canClose: false, canEdit: true },
  { code: 'XML: Ready to check', canOpen: true, canClose: true, canEdit: false, readonlyFields: [...readonlyFields] },
  { code: 'XML: Ready to open', canOpen: true, canClose: true, canEdit: false, readonlyFields: [...readonlyFields] },
  { code: 'Ready to open', canOpen: true, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Ready to be built', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Being built', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  {
    code: 'Being built (content)',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'Being built (on hold)',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  { code: 'XML: Ready to open', canOpen: true, canClose: true, canEdit: true, readonlyFields: [...readonlyFields] },

  /* ───────── AUTO-CLOSED STATES ───────── */
  {
    code: 'Auto closed (availability)',
    canOpen: true,
    canClose: true,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  { code: 'Auto closed (content)', canOpen: true, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Auto closed (geo)', canOpen: true, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Auto closed: Fraud', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  {
    code: 'XML: Auto closed content',
    canOpen: true,
    canClose: true,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },

  /* ───────── MANUAL / TEMPORARY CLOSURES ───────── */
  {
    code: 'Closed (requested by hotel)',
    canOpen: true,
    canClose: true,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'Closed - ready to reopen',
    canOpen: true,
    canClose: true,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'Closed Operations Temporary',
    canOpen: true,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'Closed Operations Permanently',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'Temp. closed for technical reasons',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },

  /* ───────── FINANCIAL / CONTRACTUAL ───────── */
  {
    code: 'Closed (invoice not paid)',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'Closed (rejected direct debit)',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'Closed - waiting DD mandate',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'RTO: Credit Control review required',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },

  /* ───────── LEGAL / FRAUD / COMPLIANCE ───────── */
  {
    code: 'Closed - legal compliance',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  { code: 'Closed Post Legal', canOpen: false, canClose: false, canEdit: false, readonlyFields: [...readonlyFields] },
  {
    code: 'Closed for Fraud - Blocking Billing',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  { code: 'Closed for fraud', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Closed for abuse', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  {
    code: 'Closed forever: technical reasons',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'Closed: Suspicious activity',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  { code: 'Auto closed: Fraud', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  {
    code: 'Business Integrity Operations',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'SB: Pending Fraud Blacklist check',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },

  /* ───────── DUPLICATE & OWNERSHIP ISSUES ───────── */
  { code: 'Duplicate', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  {
    code: 'XML: Hotel is Duplicate',
    canOpen: false,
    canClose: false,
    canEdit: false,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'Change of Ownership Closed',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },

  /* ───────── CONTRACT END / PARKED / MISC ───────── */
  { code: 'End of contract', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Parked business', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  {
    code: 'Pending approval - Booking.com BV',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  { code: 'Contract Signed', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Interested', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Not interested', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Not Eligible', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  {
    code: 'New to open (Acc. Manager)',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  { code: 'New: To be Contacted', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'SB: Invitation sent', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'SB: Not synced', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Switching currency', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Ready to be built', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Registered', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  {
    code: 'Collecting Agency Closed',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  { code: 'Closed Forever', canOpen: false, canClose: false, canEdit: false, readonlyFields: [...readonlyFields] },
  {
    code: 'Closed Forever Book-Off',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  { code: 'Closed (bankruptcy)', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Closed (complaints)', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  {
    code: 'Closed for Fraud - Blocking Billing',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },
  {
    code: 'Closed Operations Permanently',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },

  /* ───────── RTO FRAUD & GEO ───────── */
  {
    code: 'RTO: Fraud suspicious',
    canOpen: false,
    canClose: false,
    canEdit: true,
    readonlyFields: [...readonlyFields],
  },

  /* ───────── OTHER & PLACEHOLDER ───────── */
  { code: 'NULL', canOpen: false, canClose: false, canEdit: true, readonlyFields: [...readonlyFields] },
  { code: 'Test Hotel', canOpen: true, canClose: true, canEdit: true },
]

export function isBdcPropertyFraud(status: string): boolean {
  return /Fraud/i.test(status)
}

export function isBdcContentDisabled(status: string): boolean | string[] {
  const meta = bdcStatusMeta.find((meta) => meta.code === status)
  if (!meta) {
    return false
  }
  if (!meta.canEdit) {
    return true
  }
  return meta.readonlyFields?.length ? meta.readonlyFields : false
}
